import type { FlowDisplayLanguage } from '../displayLanguage';

export type LightPayload = {
  red: number;
  green: number;
  blue: number;
  mode: 0 | 1;
};

export const LIGHT_EXECUTION_DURATION_MS = 2_000;

export const LIGHT_PRESETS = [
  {
    color: 'red',
    label: { en: 'Red', ja: '赤' },
    displayColor: '#ff2828',
    payload: { red: 255, green: 0, blue: 0, mode: 1 },
  },
  {
    color: 'orange',
    label: { en: 'Orange', ja: 'オレンジ' },
    displayColor: '#ff8c00',
    payload: { red: 255, green: 27, blue: 0, mode: 1 },
  },
  {
    color: 'yellow',
    label: { en: 'Yellow', ja: '黄' },
    displayColor: '#ffdc00',
    payload: { red: 255, green: 53, blue: 0, mode: 1 },
  },
  {
    color: 'green',
    label: { en: 'Green', ja: '緑' },
    displayColor: '#00c850',
    payload: { red: 70, green: 255, blue: 0, mode: 1 },
  },
  {
    color: 'blue',
    label: { en: 'Blue', ja: '青' },
    displayColor: '#0078ff',
    payload: { red: 0, green: 0, blue: 255, mode: 1 },
  },
  {
    color: 'purple',
    label: { en: 'Purple', ja: '紫' },
    displayColor: '#8a00ff',
    payload: { red: 255, green: 0, blue: 255, mode: 1 },
  },
] as const satisfies readonly {
  color: string;
  label: Record<FlowDisplayLanguage, string>;
  displayColor: string;
  payload: LightPayload;
}[];

const LEGACY_LIGHT_OFF_PRESET = {
  color: 'off',
  label: { en: 'Off', ja: '消灯' },
  displayColor: '#000000',
  payload: { red: 0, green: 0, blue: 0, mode: 0 },
} as const satisfies {
  color: string;
  label: Record<FlowDisplayLanguage, string>;
  displayColor: string;
  payload: LightPayload;
};

export type SelectableLightColor = (typeof LIGHT_PRESETS)[number]['color'];
export type LightColor = SelectableLightColor | typeof LEGACY_LIGHT_OFF_PRESET.color;
export type LightPreset = (typeof LIGHT_PRESETS)[number] | typeof LEGACY_LIGHT_OFF_PRESET;

export const DEFAULT_LIGHT_COLOR: LightColor = 'orange';

export const isLightColor = (value: unknown): value is LightColor =>
  value === LEGACY_LIGHT_OFF_PRESET.color
  || LIGHT_PRESETS.some((preset) => preset.color === value);

export const isSelectableLightColor = (value: unknown): value is SelectableLightColor =>
  LIGHT_PRESETS.some((preset) => preset.color === value);

export const getLightPreset = (color: LightColor): LightPreset => {
  if (color === LEGACY_LIGHT_OFF_PRESET.color) return LEGACY_LIGHT_OFF_PRESET;
  const preset = LIGHT_PRESETS.find((candidate) => candidate.color === color);
  if (!preset) {
    throw new Error(`Unknown light color: ${color}`);
  }
  return preset;
};

export const getLightLabel = (
  color: LightColor,
  language: FlowDisplayLanguage,
): string => getLightPreset(color).label[language];

export const getLightPayload = (color: LightColor): LightPayload =>
  getLightPreset(color).payload;

export const getLightHexColor = (color: LightColor): string =>
  getLightPreset(color).displayColor;
